import { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Mic, MicOff, Square, Loader2, FileAudio, Check, 
  Wand2, X, Volume2, Clock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NOTE_TYPES = [
  { value: 'clinical_note', label: 'Clinical Note' },
  { value: 'nursing_note', label: 'Nursing Note' },
  { value: 'social_work_note', label: 'Social Work Note' },
  { value: 'chaplain_note', label: 'Chaplain Note' },
  { value: 'medication_note', label: 'Medication Note' },
  { value: 'symptom_note', label: 'Symptom Note' },
  { value: 'family_communication', label: 'Family Communication' },
  { value: 'care_coordination', label: 'Care Coordination' },
];

const PRIORITIES = [
  { value: 'routine', label: 'Routine', color: 'bg-gray-500' },
  { value: 'important', label: 'Important', color: 'bg-blue-500' },
  { value: 'urgent', label: 'Urgent', color: 'bg-orange-500' },
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
];

export default function VoiceNoteRecorder({ 
  chartId, 
  patientName,
  onNoteCreated,
  buttonVariant = "default",
  buttonSize = "default",
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [editedText, setEditedText] = useState('');
  const [noteType, setNoteType] = useState('clinical_note');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('routine');
  const [tags, setTags] = useState([]);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Recording started');
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Failed to access microphone. Please check permissions.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(track => track.stop());
      clearInterval(timerRef.current);
      setIsRecording(false);
      setIsPaused(false);
      toast.success('Recording stopped');
    }
  }, [isRecording]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        clearInterval(timerRef.current);
      }
      setIsPaused(!isPaused);
    }
  }, [isRecording, isPaused]);

  const transcribeAudio = useCallback(async () => {
    if (!audioBlob) {
      toast.error('No audio to transcribe');
      return;
    }
    
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');
      formData.append('language', 'en');
      formData.append('prompt', `Clinical documentation for ${patientName || 'hospice patient'}. Note type: ${noteType}`);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/voice/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Transcription failed');
      }
      
      const data = await response.json();
      setTranscription(data.text);
      setEditedText(data.text);
      
      // Auto-generate title
      const autoTitle = `Voice Note: ${data.text.substring(0, 40)}${data.text.length > 40 ? '...' : ''}`;
      setTitle(autoTitle);
      setTags(['Voice Note']);
      
      toast.success('Transcription complete!');
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Failed to transcribe audio. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  }, [audioBlob, noteType, patientName]);

  const handleSubmitToEHR = async () => {
    if (!chartId) {
      toast.error('No patient chart selected');
      return;
    }
    
    if (!editedText.trim()) {
      toast.error('Note content is required');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ehr/notes/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chart_id: chartId,
          note_type: noteType,
          title: title || 'Voice Note',
          content: editedText,
          priority: priority,
          tags: [...tags, 'Voice Note']
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save note');
      }
      
      toast.success('Voice note saved to EHR!');
      
      // Reset state
      setAudioBlob(null);
      setTranscription('');
      setEditedText('');
      setTitle('');
      setTags([]);
      setRecordingTime(0);
      setIsOpen(false);
      
      if (onNoteCreated) {
        onNoteCreated();
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to save note to EHR');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setTranscription('');
    setEditedText('');
    setRecordingTime(0);
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={() => setIsOpen(true)}
        className={className}
        data-testid="voice-note-btn"
      >
        <Mic className="w-4 h-4 mr-2" />
        Voice Note
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-cyan-500" />
              Voice Note Recorder
            </DialogTitle>
            <DialogDescription>
              Record and transcribe clinical notes using AI-powered speech recognition
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Recording Controls */}
            <div className="flex flex-col items-center gap-4 p-6 bg-slate-900 rounded-lg">
              {/* Timer Display */}
              <div className="text-4xl font-mono text-cyan-400 flex items-center gap-2">
                <Clock className="w-6 h-6" />
                {formatTime(recordingTime)}
              </div>
              
              {/* Recording Status */}
              <div className="flex items-center gap-2">
                {isRecording && !isPaused && (
                  <Badge className="bg-red-500 animate-pulse">
                    <Volume2 className="w-3 h-3 mr-1" /> Recording
                  </Badge>
                )}
                {isPaused && (
                  <Badge className="bg-yellow-500">Paused</Badge>
                )}
                {audioBlob && !isRecording && (
                  <Badge className="bg-green-500">
                    <FileAudio className="w-3 h-3 mr-1" /> Audio Ready
                  </Badge>
                )}
              </div>
              
              {/* Control Buttons */}
              <div className="flex items-center gap-3">
                {!isRecording && !audioBlob && (
                  <Button
                    size="lg"
                    onClick={startRecording}
                    className="bg-red-600 hover:bg-red-700 rounded-full w-16 h-16"
                  >
                    <Mic className="w-8 h-8" />
                  </Button>
                )}
                
                {isRecording && (
                  <>
                    <Button
                      variant="outline"
                      onClick={pauseRecording}
                      className="rounded-full w-12 h-12"
                    >
                      {isPaused ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                    </Button>
                    <Button
                      size="lg"
                      onClick={stopRecording}
                      className="bg-gray-600 hover:bg-gray-700 rounded-full w-16 h-16"
                    >
                      <Square className="w-6 h-6" />
                    </Button>
                  </>
                )}
                
                {audioBlob && !isRecording && (
                  <>
                    <Button
                      variant="outline"
                      onClick={resetRecording}
                    >
                      <X className="w-4 h-4 mr-2" /> Reset
                    </Button>
                    <Button
                      onClick={transcribeAudio}
                      disabled={isTranscribing}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      {isTranscribing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Transcribing...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2" />
                          Transcribe
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Transcription Result */}
            {transcription && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-800 rounded-lg">
                  <Label className="text-sm text-slate-400">Original Transcription</Label>
                  <p className="text-sm text-slate-300 mt-1">{transcription}</p>
                </div>
                
                {/* Editable Note */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Note Type</Label>
                      <Select value={noteType} onValueChange={setNoteType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {NOTE_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={priority} onValueChange={setPriority}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITIES.map(p => (
                            <SelectItem key={p.value} value={p.value}>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${p.color}`} />
                                {p.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Note title"
                    />
                  </div>
                  
                  <div>
                    <Label>Content (edit as needed)</Label>
                    <Textarea
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      rows={6}
                      className="font-mono text-sm"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-sm">Tags:</Label>
                    {tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {tag}
                        <X 
                          className="w-3 h-3 ml-1 cursor-pointer" 
                          onClick={() => setTags(tags.filter((_, idx) => idx !== i))}
                        />
                      </Badge>
                    ))}
                    <Badge className="bg-purple-600 text-xs">Voice Note</Badge>
                  </div>
                </div>
                
                {/* Submit Button */}
                <Button
                  onClick={handleSubmitToEHR}
                  disabled={isSubmitting || !editedText.trim()}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving to EHR...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save to Patient Chart
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Patient Info */}
            {chartId && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <AlertCircle className="w-4 h-4" />
                Saving to chart: {patientName || chartId}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
